import { useState } from "react";
import { Card, CardHeader } from "../../design-system/Card";
import { Badge } from "../../design-system/Badge";
import { Button } from "../../design-system/Button";
import { StatCard } from "../../design-system/StatCard";
import { Icon, type IconName } from "../../design-system/Icon";
import { LoadingState, ErrorState } from "../../components/States";
import { firstProblem, minLen } from "../../components/validators";
import { useIpc, useIpcQuery } from "../../ipc/IpcProvider";
import type { EngineOverviewDto, SupplierAction } from "../../ipc/types";
import { CopyDataButton } from "../../components/CopyDataButton";
import "../dashboard/dashboard.css";
import "../product-development/product-development.css";

/** Acciones disponibles según el estado mostrado en la fila. */
function actionsFor(statusLabel: string): SupplierAction[] {
  if (statusLabel === "En lista negra") return ["archive"];
  if (statusLabel === "Activo") return ["deactivate", "blacklist", "archive"];
  if (statusLabel === "Borrador" || statusLabel === "Inactivo") return ["activate", "archive"];
  return [];
}
const ACTION_LABEL: Record<SupplierAction, string> = { activate: "Activar", deactivate: "Desactivar", archive: "Archivar", blacklist: "Lista negra" };

const SUPPLIER_TYPES: readonly { value: string; label: string }[] = [
  { value: "manufacturer", label: "Fabricante" },
  { value: "distributor", label: "Distribuidor" },
  { value: "wholesaler", label: "Mayorista" },
  { value: "importer", label: "Importador" },
  { value: "service_provider", label: "Servicios" },
];

function NewSupplierForm({ onCreate, onCancel, busy }: { onCreate: (v: { code: string; name: string; type: string; country: string }) => void; onCancel: () => void; busy: boolean }): JSX.Element {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [type, setType] = useState("distributor");
  const [country, setCountry] = useState("CL");
  const hint = firstProblem([
    { ok: minLen(code, 2), msg: "Ingresa un código (mínimo 2 caracteres)" },
    { ok: minLen(name, 2), msg: "Ingresa un nombre" },
  ]);
  const valid = hint === null;
  return (
    <Card style={{ marginBottom: "var(--sp-5)" }}>
      <CardHeader title="Nuevo proveedor" subtitle="Se crea en estado Borrador; luego actívalo cuando esté listo" />
      <div className="card__body">
        <div className="pd-form">
          <div className="pd-field"><span className="pd-field__label">Código</span>
            <input className="pd-input" value={code} placeholder="Ej: PROV-006" aria-label="Código" onChange={(e) => setCode(e.target.value)} /></div>
          <div className="pd-field"><span className="pd-field__label">Tipo</span>
            <select className="pd-input" value={type} aria-label="Tipo" onChange={(e) => setType(e.target.value)}>
              {SUPPLIER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select></div>
          <div className="pd-field"><span className="pd-field__label">País</span>
            <select className="pd-input" value={country} aria-label="País" onChange={(e) => setCountry(e.target.value)}>
              {["CL", "CN", "US", "PE", "AR", "BR"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select></div>
          <div className="pd-field pd-form__full"><span className="pd-field__label">Nombre</span>
            <input className="pd-input" value={name} placeholder="Ej: TechImport Ltda." aria-label="Nombre" onChange={(e) => setName(e.target.value)} /></div>
        </div>
        <div className="pd-actions">
          {hint ? <span className="faint" style={{ fontSize: 12.5, marginRight: "auto" }}>{hint}</span> : null}
          <Button variant="ghost" size="sm" onClick={onCancel}>Cancelar</Button>
          <Button size="sm" icon="check" disabled={!valid || busy} onClick={() => onCreate({ code: code.trim(), name: name.trim(), type, country })}>
            {busy ? "Creando…" : "Crear proveedor"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

export function SupplierScreen(): JSX.Element {
  const client = useIpc();
  const { state, reload } = useIpcQuery<EngineOverviewDto>((c) => c.getEngineOverview("supplier"), []);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  if (state.status === "loading") return <LoadingState />;
  if (state.status === "error") return <ErrorState message={state.error.message} onRetry={reload} />;

  const data = state.data;

  const create = async (v: { code: string; name: string; type: string; country: string }): Promise<void> => {
    setBusy(true); setMsg(null);
    const res = await client.createSupplier(v);
    setBusy(false);
    if (res.ok && res.value.ok) { setShowForm(false); setMsg(`Proveedor "${v.name}" creado.`); reload(); }
    else setMsg(res.ok ? res.value.message ?? "No se pudo crear" : res.error.message);
  };
  const act = async (id: string, action: SupplierAction): Promise<void> => {
    setBusy(true); setMsg(null);
    const res = await client.setSupplierStatus(id, action);
    setBusy(false);
    if (res.ok && res.value.ok) reload();
    else setMsg(res.ok ? res.value.message ?? "Acción no permitida" : res.error.message);
  };

  return (
    <div>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="row" style={{ gap: "var(--sp-4)" }}>
            <div className="sidebar__logo" style={{ width: 44, height: 44 }}><Icon name="supplier" size={22} /></div>
            <div>
              <h1 className="pagehead__title">Proveedores</h1>
              <p className="pagehead__sub">Ficha maestra de proveedores. Crea y gestiona; se guardan en tu equipo.</p>
            </div>
          </div>
          <div className="row">
            <CopyDataButton data={{ title: data.title, columns: data.columns, rows: data.rows, metrics: data.metrics }} />
            <Button size="sm" icon="supplier" onClick={() => setShowForm((s) => !s)}>Nuevo proveedor</Button>
          </div>
        </div>
      </div>

      <div className="kpis" style={{ marginTop: 0 }}>
        {data.metrics.map((m) => (
          <StatCard key={m.label} label={m.label} value={m.value} icon={m.icon as IconName} accent={m.accent}
            {...(typeof m.deltaPct === "number" ? { deltaPct: m.deltaPct } : {})} />
        ))}
      </div>

      {showForm ? <div style={{ marginTop: "var(--sp-5)" }}><NewSupplierForm onCreate={create} onCancel={() => setShowForm(false)} busy={busy} /></div> : null}

      <Card style={{ marginTop: "var(--sp-5)" }}>
        <CardHeader title="Directorio" subtitle={`${data.rows.length} proveedores`} action={msg ? <span className="muted" style={{ fontSize: 12.5 }}>{msg}</span> : null} />
        <div className="card__body" style={{ padding: 0 }}>
          {data.rows.length === 0 ? (
            <div className="statecard"><Icon name="supplier" size={26} style={{ color: "var(--text-faint)" }} /><div className="strong">Aún no tienes proveedores</div><div className="faint" style={{ fontSize: 13 }}>Crea el primero con “Nuevo proveedor”.</div></div>
          ) : (
            <table className="table">
              <thead><tr><th>Proveedor</th><th>Detalle</th><th>Estado</th><th>Acciones</th></tr></thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id}>
                    <td><div className="strong">{r.primary}</div></td>
                    <td className="muted">{r.secondary}</td>
                    <td><Badge tone={r.status.tone} dot>{r.status.label}</Badge></td>
                    <td>
                      <div className="row" style={{ gap: 6 }}>
                        {actionsFor(r.status.label).map((a) => (
                          <Button key={a} variant={a === "activate" ? "secondary" : "ghost"} size="sm" disabled={busy} onClick={() => act(r.id, a)}>{ACTION_LABEL[a]}</Button>
                        ))}
                        {actionsFor(r.status.label).length === 0 ? <span className="faint" style={{ fontSize: 12 }}>—</span> : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  );
}
